import React, { useState, useEffect, useRef } from 'react';
import { IonButton, IonContent, IonIcon, IonInput, IonItem, IonLabel, IonList, IonTextarea, IonDatetime, IonChip, IonPopover, IonCheckbox } from '@ionic/react';
import { sparklesOutline, repeat, calendarOutline, timeOutline } from 'ionicons/icons';
import { State } from '@meraki-internal/state';
import { ITask, createTaskDueDate, extractTimeFromDueDate, TaskRecurrenceOption, TasksState } from './TasksState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { TagsField } from '../tags/TagsField';
import { AIIcon } from '../theme/Icons';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';
import { NotePinsState } from '../notes/pins/NotePinsState';
import { Modal } from '../components/Modal';
import { PushNotificationService } from '../push-notifications/PushNotificationService';
import { AlertPresenter } from '../AlertBinder';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import dayjs from 'dayjs';
import { useDevSettings } from '../utils/useDevSettings';

interface ITaskEditorModalState {
    isOpen: boolean;
    taskId?: string;
    mode: 'add' | 'edit';
    defaultDueDate?: string;
}

export class TaskEditorModal extends State<ITaskEditorModalState> {
    static inject = () => [MixPanelEventEmitter, ViewNoteWithQueryParamDrawer, NotePinsState];

    constructor(public mixpanel: MixPanelEventEmitter, public viewNoteDrawer: ViewNoteWithQueryParamDrawer, public notePinsState: NotePinsState) {
        super({
            isOpen: false,
            mode: 'add'
        });
    }

    private openedFrom?: string;
    private hadChanges = false;
    private saveAttempted = false;
    private closeTracked = false;

    getOpenedFrom = () => this.openedFrom;

    open = (params?: { taskId?: string; defaultDueDate?: string; source?: 'add-button' | 'row-click' | 'menu-edit' | 'note-view' | 'task-review-card' }) => {
        const mode = params?.taskId ? 'edit' : 'add';
        const source = params?.source || 'unknown';

        this.openedFrom = source;
        this.hadChanges = false;
        this.saveAttempted = false;
        this.closeTracked = false;
        this.setState({
            isOpen: true,
            taskId: params?.taskId,
            mode: mode,
            defaultDueDate: params?.defaultDueDate
        });

        // Track editor opened
        try {
            this.mixpanel.track('Task Editor Opened', () => ({
                'Mode': mode,
                'Source': source
            }));
        } catch (error) {
            console.error('Failed to track Task Editor Opened:', error);
        }
    };

    private reminderEnabled?: boolean;

    setReminderState = (enabled: boolean) => {
        this.reminderEnabled = enabled;
    };

    close = () => {
        // Track editor closed only once
        if (!this.closeTracked) {
            this.closeTracked = true;
            try {
                this.mixpanel.track('Task Editor Closed', () => ({
                    'Mode': this.state.mode,
                    'Had Changes': this.hadChanges,
                    'Save Attempted': this.saveAttempted,
                    'Notification Reminder': this.reminderEnabled ? 'enabled' : 'disabled'
                }));
            } catch (error) {
                console.error('Failed to track Task Editor Closed:', error);
            }
        }

        this.reminderEnabled = undefined;
        this.setState({
            isOpen: false,
            taskId: undefined,
            defaultDueDate: undefined
        });
    };

    // Methods to track changes and save attempts
    markHasChanges = () => {
        this.hadChanges = true;
    };

    markSaveAttempted = () => {
        this.saveAttempted = true;
    };
}

interface TaskEditorModalProps {
    modal: TaskEditorModal;
    onSave: (task: Partial<ITask>, options?: { reminderEnabled?: boolean }) => Promise<string | undefined>;
    existingTask?: ITask;
    tasksState: TasksState;
}

const commonTasks = [
    'Give medication',
    'Schedule appointment',
    'Pick up prescription',
    'Arrange transportation',
    'Prepare meals',
    'Update care team'
];

const TaskEditorModalComponent: React.FC<TaskEditorModalProps> = ({ modal, onSave, existingTask, tasksState }) => {
    const [description, setDescription] = useState('');
    const [details, setDetails] = useState('');
    const [dueDate, setDueDate] = useState<string>(dayjs().startOf('day').toISOString());
    const [time, setTime] = useState<string>('');
    const [tags, setTags] = useState<string[]>([]);
    const [showTimePickerOnly, setShowTimePickerOnly] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [recurrenceType, setRecurrenceType] = useState<TaskRecurrenceOption>('none');
    const [showRecurrencePopover, setShowRecurrencePopover] = useState(false);
    const [showDatePopover, setShowDatePopover] = useState(false);
    const [showTimePopover, setShowTimePopover] = useState(false);
    const [reminderEnabled, setReminderEnabled] = useState(false);
    const [initialReminderEnabled, setInitialReminderEnabled] = useState(false);
    const recurrenceButtonRef = useRef<HTMLIonButtonElement>(null);
    const dateButtonRef = useRef<HTMLIonButtonElement>(null);

    const pushNotificationService = useInstance(PushNotificationService);
    const alertPresenter = useInstance(AlertPresenter);
    const devSettings = useDevSettings();

    // Sync reminder state to modal class for tracking
    useEffect(() => {
        modal.setReminderState(reminderEnabled);
    }, [reminderEnabled, modal]);

    const isDateInPast = (dateValue: string): boolean => {
        const taskDate = dayjs(dateValue).startOf('day');
        const today = dayjs().startOf('day');
        return taskDate.isBefore(today);
    };

    const isTimeInPast = (dateValue: string, timeValue: string): boolean => {
        if (!timeValue) {
            return false;
        }
        const taskDateTime = dayjs(createTaskDueDate(dateValue, timeValue));
        return taskDateTime.isBefore(dayjs());
    };

    const canEnableReminder = (): boolean => {
        return time.length > 0 && !isTimeInPast(dueDate, time);
    };

    const canAddTime = (): boolean => {
        return !isDateInPast(dueDate);
    };

    // Track the last state we fired an event for, to avoid duplicate events
    // but ensure we fire when toggling back and forth
    const [lastTrackedReminderState, setLastTrackedReminderState] = useState<boolean | null>(null);

    const handleReminderCheckboxChange = async (checked: boolean) => {
        if (checked) {
            const shouldRequest = await pushNotificationService.shouldRequest();

            if (shouldRequest) {
                const granted = await pushNotificationService.requestPermission();

                if (granted) {
                    setReminderEnabled(true);
                    modal.markHasChanges();
                    // Track for existing tasks only, when state differs from last tracked
                    if (existingTask && lastTrackedReminderState !== true) {
                        modal.mixpanel.track('Task Reminder Enabled', () => ({
                            'Task ID': existingTask.taskId,
                            'Has Recurrence': !!recurrenceType && recurrenceType !== 'none',
                            'Time': time || '',
                            'Due Date': dueDate || ''
                        }));
                        setLastTrackedReminderState(true);
                    }
                } else {
                    alertPresenter.showAlert({
                        header: 'Notifications Disabled',
                        message: 'Please enable notifications in your device settings to receive task reminders.',
                        buttons: [
                            'No Thanks',
                            {
                                text: 'Open Settings',
                                handler: () => {
                                    NativeSettings.open({
                                        optionIOS: IOSSettings.App,
                                        optionAndroid: AndroidSettings.ApplicationDetails
                                    });
                                }
                            }
                        ]
                    });
                }
            } else {
                setReminderEnabled(true);
                modal.markHasChanges();
                // Track for existing tasks only, when state differs from last tracked
                if (existingTask && lastTrackedReminderState !== true) {
                    modal.mixpanel.track('Task Reminder Enabled', () => ({
                        'Task ID': existingTask.taskId,
                        'Has Recurrence': !!recurrenceType && recurrenceType !== 'none',
                        'Time': time || '',
                        'Due Date': dueDate || ''
                    }));
                    setLastTrackedReminderState(true);
                }
            }
        } else {
            setReminderEnabled(false);
            modal.markHasChanges();
            // Track for existing tasks only, when state differs from last tracked
            if (existingTask && lastTrackedReminderState !== false) {
                modal.mixpanel.track('Task Reminder Disabled', () => ({
                    'Task ID': existingTask.taskId,
                    'Reason': 'manual',
                    'Has Recurrence': !!recurrenceType && recurrenceType !== 'none',
                    'Time': time || '',
                    'Due Date': dueDate || ''
                }));
                setLastTrackedReminderState(false);
            }
        }
    };

    useEffect(() => {
        if (modal.state.isOpen && existingTask && modal.state.mode === 'edit') {
            setDescription(existingTask.description);
            setDetails(existingTask.details || '');
            setTags(existingTask.tags || []);
            setRecurrenceType(existingTask.recurrenceRule?.type || 'none');

            // Extract date and time from existing dueDate
            const taskDueDate = existingTask.dueDate || dayjs().startOf('day').toISOString();
            setDueDate(taskDueDate);

            // Extract time if dueDate includes time information
            const extractedTime = extractTimeFromDueDate(taskDueDate);
            if (extractedTime) {
                setTime(extractedTime);
                setShowTimePickerOnly(true);
            } else {
                setTime('');
                setShowTimePickerOnly(false);
            }

            // Load reminder preference when opening modal in edit mode
            if (existingTask.taskId) {
                tasksState.getReminderPreference(existingTask.taskId)
                    .then(result => {
                        setReminderEnabled(result.enabled);
                        setInitialReminderEnabled(result.enabled);
                        // Initialize tracking state to the loaded value
                        setLastTrackedReminderState(result.enabled);
                    })
                    .catch(error => {
                        console.error('Failed to load reminder preference:', error);
                        setReminderEnabled(false);
                        setInitialReminderEnabled(false);
                        setLastTrackedReminderState(false);
                    });
            }
        } else if (modal.state.isOpen && modal.state.mode === 'add') {
            // Reset form for new task
            setDescription('');
            setDetails('');
            setTags([]);
            setRecurrenceType('none');
            setReminderEnabled(false);
            setInitialReminderEnabled(false);
            // For new tasks, set to null so no events fire on toggle
            setLastTrackedReminderState(null);
            // Use defaultDueDate if provided, otherwise use today
            setDueDate(modal.state.defaultDueDate || dayjs().startOf('day').toISOString());
            setTime('');
            setShowTimePickerOnly(false);
            setShowSuggestions(true);
        }
    }, [modal.state.isOpen, existingTask, modal.state.mode, modal.state.defaultDueDate, tasksState]);

    const handleSave = async () => {
        modal.markSaveAttempted();
        const task: Partial<ITask> = {
            description,
            details: details,
            dueDate: createTaskDueDate(dueDate, time || undefined),
            tags: tags.length > 0 ? tags : undefined,
            completed: false,
            recurrenceRule: recurrenceType !== 'none' ? { type: recurrenceType } : undefined
        };


        if (modal.state.mode === 'edit' && existingTask) {
            // When editing, preserve all existing fields and only override the ones we're changing
            Object.assign(task, {
                taskId: existingTask.taskId,
                displayOrder: existingTask.displayOrder,
                source: existingTask.source,
                completedAt: existingTask.completedAt,
                completedBy: existingTask.completedBy
            });
        }

        // Save the task first and get the taskId
        // Pass reminderEnabled for new tasks so tracking is accurate
        const isNewTask = modal.state.mode === 'add';
        const taskId = await onSave(task, isNewTask ? { reminderEnabled } : undefined);

        // Save reminder preference if it changed (either enabled with time, or disabled)
        if (taskId && reminderEnabled !== initialReminderEnabled) {
            try {
                await tasksState.setReminderPreference(taskId, reminderEnabled);
            } catch (error) {
                console.error('Failed to save reminder preference:', error);
                // Don't block closing the modal if reminder save fails
            }
        }

        modal.close();
    };

    const handleSuggestionClick = (taskName: string) => {
        setDescription(taskName);
        setShowSuggestions(false);
        modal.markHasChanges();
    };

    // Wrapper functions to track changes
    const handleDescriptionChange = (value: string) => {
        setDescription(value);
        modal.markHasChanges();
    };

    const handleDetailsChange = (value: string) => {
        setDetails(value);
        modal.markHasChanges();
    };

    const handleDueDateChange = (value: string) => {
        const wasInPast = time && isTimeInPast(dueDate, time);
        setDueDate(value);
        modal.markHasChanges();

        // If time exists and was in past but now in future, re-enable reminder
        if (time && wasInPast && !isTimeInPast(value, time)) {
            setReminderEnabled(true);
        }
        // If time exists and was in future but now in past, disable reminder
        else if (time && !wasInPast && isTimeInPast(value, time)) {
            setReminderEnabled(false);
        }
    };

    const handleTimeChange = (value: string) => {
        const oldTime = time;
        const wasInPast = oldTime && isTimeInPast(dueDate, oldTime);
        setTime(value);
        modal.markHasChanges();

        if (value && !oldTime) {
            // Adding time for first time - enable reminder
            setReminderEnabled(true);
        } else if (!value && oldTime) {
            // Removing time - disable reminder
            setReminderEnabled(false);
            // Track auto-disable for existing tasks, when state differs from last tracked
            if (existingTask && lastTrackedReminderState !== false) {
                modal.mixpanel.track('Task Reminder Disabled', () => ({
                    'Task ID': existingTask.taskId,
                    'Reason': 'auto',
                    'Has Recurrence': !!recurrenceType && recurrenceType !== 'none',
                    'Time': value || '',
                    'Due Date': dueDate || ''
                }));
                setLastTrackedReminderState(false);
            }
        } else if (value && isTimeInPast(dueDate, value)) {
            // Time is in the past - disable reminder
            setReminderEnabled(false);
            // Track auto-disable for existing tasks, when state differs from last tracked
            if (existingTask && lastTrackedReminderState !== false) {
                modal.mixpanel.track('Task Reminder Disabled', () => ({
                    'Task ID': existingTask.taskId,
                    'Reason': 'auto',
                    'Has Recurrence': !!recurrenceType && recurrenceType !== 'none',
                    'Time': value || '',
                    'Due Date': dueDate || ''
                }));
                setLastTrackedReminderState(false);
            }
        } else if (value && wasInPast && !isTimeInPast(dueDate, value)) {
            // Time was in past, now in future - re-enable reminder
            setReminderEnabled(true);
        }
    };

    const handleTagsChange = (value: string[]) => {
        setTags(value);
        modal.markHasChanges();
    };

    const handleRecurrenceTypeChange = (value: TaskRecurrenceOption) => {
        setRecurrenceType(value);
        modal.markHasChanges();
    };

    const getDateDisplayText = (dateValue: string) => {
        const date = dayjs(dateValue);
        const today = dayjs().startOf('day');
        const tomorrow = today.add(1, 'day');

        if (date.isSame(today, 'day')) {
            return 'Today';
        } else if (date.isSame(tomorrow, 'day')) {
            return 'Tomorrow';
        } else {
            return date.format('MMM D');
        }
    };

    const isFormValid = description.trim().length > 0;

    return (
        <Modal
            maximizeDesktopHeight
            isOpen={modal.state.isOpen}
            closeHandler={() => modal.close()}
            title={modal.state.mode === 'edit' ? 'Edit Task' : 'Add New Task'}
            data-id="task-editor-modal"
        >
            <IonContent>
                <div style={{ padding: '16px' }}>
                    {modal.state.mode === 'edit' && existingTask?.source?.type === 'ai-generated' && (
                        <div
                            style={{
                                marginBottom: '20px',
                                padding: '12px',
                                backgroundColor: 'var(--ion-color-step-50)',
                                borderRadius: '8px',
                                cursor: existingTask?.source?.noteId ? 'pointer' : 'default',
                                transition: 'background-color 0.2s ease',
                                border: '1px solid var(--ion-color-step-100)'
                            }}
                            onClick={existingTask?.source?.noteId ? (e: React.MouseEvent) => {
                                e.stopPropagation();
                                // Track when user clicks to view source note
                                modal.mixpanel.track('Task Source Note Opened', () => ({
                                    'Task ID': existingTask.taskId,
                                    'Note ID': existingTask.source!.noteId!,
                                    'Notification Reminder': reminderEnabled ? 'enabled' : 'disabled',
                                    'Is Pinned': modal.notePinsState.isPinned(existingTask.source!.noteId!)
                                }));
                                // Close the task editor first, then open the note drawer
                                modal.close();
                                setTimeout(() => {
                                    modal.viewNoteDrawer.open({
                                        noteId: existingTask.source!.noteId!,
                                        trackingFrom: 'Task Editor'
                                    });
                                }, 100);
                            } : undefined}
                            onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                                if (existingTask?.source?.noteId) {
                                    e.currentTarget.style.backgroundColor = 'var(--ion-color-step-100)';
                                }
                            }}
                            onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                                e.currentTarget.style.backgroundColor = 'var(--ion-color-step-50)';
                            }}
                            data-id="task-editor-source-note-banner"
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
                                <AIIcon size="small" color="primary" />
                                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--ion-color-step-800)' }}>
                                    Generated from recording.
                                </span>
                                {existingTask?.source?.noteId && (
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--ion-color-primary)' }}>
                                        Go to summary →
                                    </span>
                                )}
                            </div>
                        </div>
                    )}

                    {modal.state.mode === 'add' && showSuggestions && (
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                <IonIcon icon={sparklesOutline} color="primary" />
                                <IonLabel style={{ fontSize: '16px', fontWeight: '600' }}>
                                    Common care tasks
                                </IonLabel>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {commonTasks.map((task, index) => (
                                    <IonChip
                                        key={index}
                                        onClick={() => handleSuggestionClick(task)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        {task}
                                    </IonChip>
                                ))}
                            </div>
                        </div>
                    )}

                    <IonList>
                        <IonItem>
                            <IonLabel position="stacked">Task Name *</IonLabel>
                            <IonInput
                                value={description}
                                onIonInput={e => handleDescriptionChange(e.detail.value!)}
                                onIonChange={e => handleDescriptionChange(e.detail.value!)}
                                placeholder="What needs to be done?"
                                data-id="task-description-input"
                            />
                        </IonItem>

                        <IonItem lines="none">
                            <IonLabel position="stacked">Date</IonLabel>
                            <div style={{ padding: '12px 0' }}>
                                <IonButton
                                    ref={dateButtonRef}
                                    id="date-trigger-button"
                                    fill="outline"
                                    size="default"
                                    onClick={() => setShowDatePopover(true)}
                                    style={{ width: '140px' }}
                                    data-id="date-button"
                                >
                                    <IonIcon icon={calendarOutline} slot="start" />
                                    {getDateDisplayText(dueDate)}
                                </IonButton>
                            </div>
                        </IonItem>

                        <IonItem lines="none">
                            <IonLabel position="stacked">Time</IonLabel>
                            <div style={{ padding: '12px 0' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {!showTimePickerOnly ? (
                                        <IonButton
                                            id="time-trigger-button"
                                            fill="outline"
                                            size="default"
                                            disabled={!canAddTime()}
                                            onClick={() => {
                                                // Set initial time to current time rounded to next 15-minute interval
                                                const now = dayjs();
                                                const minutes = now.minute();
                                                const roundedMinutes = Math.ceil(minutes / 15) * 15;
                                                // Add an hour if minutes overflow past 60
                                                const hoursToAdd = Math.floor(roundedMinutes / 60);
                                                const initialTime = now.add(hoursToAdd, 'hour').minute(roundedMinutes % 60).second(0);
                                                handleTimeChange(initialTime.format('h:mm A'));
                                                setShowTimePickerOnly(true);
                                                setShowTimePopover(true);
                                            }}
                                            style={{ width: '140px' }}
                                            data-id="add-time-button"
                                        >
                                            <IonIcon icon={timeOutline} slot="start" />
                                            Add Time
                                        </IonButton>
                                    ) : (
                                        <>
                                            <IonButton
                                                id="time-trigger-button"
                                                fill="outline"
                                                size="default"
                                                disabled={isDateInPast(dueDate)}
                                                onClick={() => setShowTimePopover(true)}
                                                style={{ width: '140px' }}
                                                data-id="time-button"
                                            >
                                                <IonIcon icon={timeOutline} slot="start" />
                                                {time}
                                            </IonButton>
                                            <IonButton
                                                fill="clear"
                                                size="small"
                                                onClick={() => {
                                                    handleTimeChange('');
                                                    setShowTimePickerOnly(false);
                                                }}
                                                data-id="remove-time-button"
                                            >
                                                Remove
                                            </IonButton>
                                        </>
                                    )}
                                </div>
                            </div>
                        </IonItem>
                        <div style={{ display: 'flex', alignItems: 'center', padding: '0 16px 12px 20px' }}>
                            <IonCheckbox
                                checked={reminderEnabled}
                                disabled={!canEnableReminder()}
                                onIonChange={e => handleReminderCheckboxChange(e.detail.checked)}
                                data-id="reminder-checkbox"
                                style={{ marginRight: 8 }}
                            />
                            <span style={{ opacity: canEnableReminder() ? 1 : 0.5 }}>
                                Reminder at time of task
                            </span>
                        </div>

                        <IonItem style={{ marginTop: '6px' }}>
                            <IonLabel position="stacked">Repeat</IonLabel>
                            <div style={{ padding: '12px 0' }}>
                                <IonButton
                                    ref={recurrenceButtonRef}
                                    id="recurrence-trigger-button"
                                    fill="outline"
                                    size="default"
                                    onClick={() => setShowRecurrencePopover(true)}
                                    style={{ width: '140px' }}
                                    data-id="repeat-button"
                                >
                                    <IonIcon icon={repeat} slot="start" />
                                    {recurrenceType === 'none' ? 'Repeat' :
                                        recurrenceType === 'daily' ? 'Daily' :
                                            recurrenceType === 'weekly' ? 'Weekly' : 'Monthly'}
                                </IonButton>
                            </div>
                        </IonItem>

                        <IonItem>
                            <IonLabel position="stacked">Additional Details</IonLabel>
                            <IonTextarea
                                value={details}
                                onIonInput={e => handleDetailsChange(e.detail.value!)}
                                onIonChange={e => handleDetailsChange(e.detail.value!)}
                                placeholder="Any special instructions or notes"
                                rows={3}
                                data-id="task-details-input"
                            />
                        </IonItem>
                    </IonList>

                    {/* Tags section */}
                    <div style={{ marginTop: '16px' }}>
                        <TagsField
                            value={tags}
                            onChange={handleTagsChange}
                            entityType="task"
                        />
                    </div>

                    <div style={{ marginTop: '20px', padding: '0 16px' }}>
                        <IonButton
                            expand="block"
                            onClick={handleSave}
                            disabled={!isFormValid}
                            data-id="task-save-button"
                        >
                            {modal.state.mode === 'edit' ? 'Save Changes' : 'Add Task'}
                        </IonButton>
                    </div>
                </div>
            </IonContent>

            {/* Recurrence Popover */}
            <IonPopover
                trigger="recurrence-trigger-button"
                isOpen={showRecurrencePopover}
                onDidDismiss={() => setShowRecurrencePopover(false)}
                alignment="start"
                data-id="recurrence-popover"
            >
                <IonList>
                    <IonItem
                        button
                        lines="none"
                        onClick={() => {
                            handleRecurrenceTypeChange('daily');
                            setShowRecurrencePopover(false);
                        }}
                    >
                        <IonLabel>Daily</IonLabel>
                    </IonItem>
                    <IonItem
                        button
                        lines="none"
                        onClick={() => {
                            handleRecurrenceTypeChange('weekly');
                            setShowRecurrencePopover(false);
                        }}
                    >
                        <IonLabel>Weekly</IonLabel>
                    </IonItem>
                    <IonItem
                        button
                        lines="none"
                        onClick={() => {
                            handleRecurrenceTypeChange('monthly');
                            setShowRecurrencePopover(false);
                        }}
                    >
                        <IonLabel>Monthly</IonLabel>
                    </IonItem>
                    {recurrenceType !== 'none' && (
                        <>
                            <IonItem lines="full" style={{ height: '1px', minHeight: '1px' }}>
                                <div style={{ width: '100%', height: '1px', backgroundColor: 'var(--ion-color-light)' }} />
                            </IonItem>
                            <IonItem
                                button
                                lines="none"
                                onClick={() => {
                                    handleRecurrenceTypeChange('none');
                                    setShowRecurrencePopover(false);
                                }}
                            >
                                <IonLabel color="medium">Clear</IonLabel>
                            </IonItem>
                        </>
                    )}
                </IonList>
            </IonPopover>

            {/* Date Popover */}
            <IonPopover
                trigger="date-trigger-button"
                isOpen={showDatePopover}
                onDidDismiss={() => setShowDatePopover(false)}
                alignment="start"
                data-id="date-popover"
                style={{
                    '--width': '365px',
                    '--min-width': '365px',
                    '--max-width': '90vw'
                } as React.CSSProperties}
            >
                <div style={{ padding: '16px' }}>
                    <IonDatetime
                        value={dueDate}
                        onIonChange={e => {
                            handleDueDateChange(e.detail.value as string);
                            setShowDatePopover(false);
                        }}
                        presentation="date"
                        data-id="task-due-date-select"
                    />
                </div>
            </IonPopover>

            {/* Time Popover */}
            <IonPopover
                trigger="time-trigger-button"
                isOpen={showTimePopover}
                onDidDismiss={() => setShowTimePopover(false)}
                alignment="start"
                data-id="time-popover"
            >
                <IonDatetime
                    value={time ? (() => {
                        // Convert 12-hour format to 24-hour format for IonDatetime
                        const parsed = dayjs(`2024-01-01 ${time}`, 'YYYY-MM-DD h:mm A');
                        return parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm:ss') : undefined;
                    })() : undefined}
                    onIonChange={e => {
                        const dateValue = e.detail.value as string;
                        const timeValue = dayjs(dateValue).format('h:mm A');
                        handleTimeChange(timeValue);
                    }}
                    presentation="time"
                    hourCycle="h12"
                    minuteValues={devSettings.showTaskNotificationTestBtns ? '0,5,10,15,20,25,30,35,40,45,50,55' : '0,15,30,45'}
                    data-id="task-when-input"
                />
            </IonPopover>
        </Modal>
    );
};

export { TaskEditorModalComponent };
